import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge } from "@dl/web-core-js/ui";
import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../api/manufacturing";
import type { ScrapRow } from "../../api/production";

const SQUARE_METRES = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PERCENT = new Intl.NumberFormat(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** mm², as the server counts panel area, shown in m². */
export function squareMetres(mm2: number | null | undefined): string {
  return mm2 == null ? "" : `${SQUARE_METRES.format(Number(mm2) / 1_000_000)} m²`;
}

/** A figure of a row in its unit: m² for a panel, the stock unit for anything else. */
function amount(row: ScrapRow, value: number | null): string {
  if (value == null) return "";
  return row.kind === "PANEL" ? squareMetres(value) : `${formatNumber(value)} ${row.unit_code ?? ""}`.trim();
}

/** "2362-580: 6 sheets · 2362-581: 2 sheets", what a virtual panel was really cut from. */
function sheetsOf(t: TFunction, row: ScrapRow): string {
  return (row.real_parts ?? [])
    .filter((real) => Number(real.sheets) !== 0)
    .map((real) => t("scrap.sheets", "{{part}}: {{sheets}} sheets", { part: real.part_number, sheets: formatNumber(real.sheets) }))
    .join(" · ");
}

/**
 * Engineering need against consumption, one row per virtual panel and per other
 * material. The order's (or the period's) scrap is the difference, stated only
 * for completed orders: before that, consumption is still running.
 */
export function ScrapTable({ rows, loading, showOrderCount = false }: { rows: ScrapRow[]; loading: boolean; showOrderCount?: boolean }) {
  const { t } = useTranslation();
  const cols = useMemo(() => {
    const c = defineColumns<ScrapRow>();
    return [
      c.custom(
        "part",
        (row) => row.part_number,
        (row) => {
          const sheets = row.kind === "PANEL" ? sheetsOf(t, row) : "";
          return (
            <span className="flex flex-col">
              <span>
                <span className="font-medium">{row.part_number}</span>
                <span className="ml-2 text-[11px] text-ink-faint">{row.part_name}</span>
              </span>
              {sheets !== "" && <span className="text-[11px] text-ink-muted">{sheets}</span>}
            </span>
          );
        },
        { header: t("scrap.column.material", "Material"), priority: 1 },
      ),
      c.custom(
        "kind",
        (row) => row.kind,
        (row) => (
          <Badge variant={row.kind === "PANEL" ? "info" : "neutral"} size="sm">
            {row.kind === "PANEL" ? t("scrap.kind.panel", "Panel") : t("scrap.kind.material", "Material")}
          </Badge>
        ),
        {
          header: t("scrap.column.kind", "Kind"),
          priority: 2,
          filter: "select",
          filterOptions: [
            { value: "PANEL", label: t("scrap.kind.panel", "Panel") },
            { value: "MATERIAL", label: t("scrap.kind.material", "Material") },
          ],
        },
      ),
      c.custom("engineering", (row) => Number(row.engineering ?? 0), (row) => <span className="tabular-nums">{amount(row, row.engineering)}</span>, {
        header: t("scrap.column.engineering", "Engineering"),
        priority: 1,
        align: "right",
      }),
      c.custom("consumed", (row) => Number(row.consumed ?? 0), (row) => <span className="tabular-nums">{amount(row, row.consumed)}</span>, {
        header: t("scrap.column.consumed", "Consumed"),
        priority: 1,
        align: "right",
      }),
      c.custom(
        "scrap",
        (row) => (row.scrap == null ? Number.NEGATIVE_INFINITY : Number(row.scrap)),
        (row) => <span className={Number(row.scrap ?? 0) < 0 ? "tabular-nums text-ok" : "tabular-nums"}>{amount(row, row.scrap)}</span>,
        { header: t("scrap.column.scrap", "Scrap"), priority: 1, align: "right" },
      ),
      c.custom(
        "scrap_percent",
        (row) => (row.scrap_percent == null ? Number.NEGATIVE_INFINITY : Number(row.scrap_percent)),
        (row) => <span className="tabular-nums">{row.scrap_percent == null ? "" : `${PERCENT.format(Number(row.scrap_percent))} %`}</span>,
        { header: t("scrap.column.percent", "Scrap %"), priority: 2, align: "right" },
      ),
      ...(showOrderCount ? [c.number("order_count", (row) => row.order_count, { header: t("scrap.column.orders", "Orders"), priority: 3 })] : []),
    ];
  }, [t, showOrderCount]);

  return (
    <DataTable<ScrapRow>
      data={rows}
      columns={cols}
      getRowId={(row) => `${row.kind}-${String(row.part_id)}`}
      loading={loading}
      emptyMessage={t("scrap.empty", "Nothing consumed yet.")}
    />
  );
}
